import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Nav } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { upperFirst } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';
import Table from '../../../components/Table';
import ExaminationModel from './examination-model';

import { resetExaminations } from '../../../redux/examination/slice';
import { getExaminationDetails, getExaminations } from '../../../redux/examination';
import { useExaminationColumn, useSysExaminationType } from './hook';
import useDebounce from '../../../utils/useDebounceHook';


const SysExaminations = () => {
	const [type, setType] = useState('general');
	const [searchText, setSearchText] = useState('');
	const [model, setModel] = useState(false);
	const [perPage, setPerPage] = useState(10);
	const [columnSort, setColumnSort] = useState({});
	const [currentPage, setCurrentPage] = useState(1);
	const search = useDebounce(searchText, 500);
	const { loading, examinations, total } = useSelector(state => state.examinations);
	const dispatch = useDispatch();

	useEffect(() => {
		fetchExamination();
		return () => {
			dispatch(resetExaminations());
		};
	}, [currentPage, perPage, search, columnSort, type, dispatch]);

	const changeType = useCallback((type) => {
		setSearchText('');
		setPerPage(10);
		setCurrentPage(1);
		setColumnSort({});
		setType(type);
	}, []);

	const fetchExamination = useCallback(() => {
		dispatch(getExaminations({ type, page: currentPage, per_page: perPage, search, ...columnSort }));
	}, [currentPage, perPage, search, type, columnSort, dispatch]);

	const handlePerRowsChange = useCallback(async(newPerPage) => {
		setPerPage(newPerPage);
		setCurrentPage(1);
	}, []);

	const handlePageChange = useCallback(page => {
		setCurrentPage(page);
	}, []);

	const handleSort = useCallback(
		(column, sortDirection) => {
			setColumnSort({ column_name: column.sortField, order: sortDirection });
		},
		[setColumnSort]
	);

	const fetchExaminationDetails = useCallback(
		(id) => {
			dispatch(getExaminationDetails({ id }))
				.unwrap()
				.then(() => setModel(true));
		},
		[dispatch]
	);
	const columns = useExaminationColumn({ t, currentPage, handleEdit: fetchExaminationDetails, perPage });

	const examinationTypes = useSysExaminationType({ t });

	return (
		<Card>
			<Card.Header className='bg-white fw-bold d-flex justify-content-between align-items-center'>
				<span>{t('EXAMINATIONS.TITLE', { type: upperFirst(type) })}</span>
				<div className='d-flex align-items-center'>
					<Form.Control
						type="search"
						placeholder={t('GENERAL.SEARCHTEXT')}
						size="sm"
						className='form-control d-inline-block w-auto me-2'
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
					/>
					<Button onClick={() => setModel(true)} variant="success" size="sm" className='d-inline-flex align-items-center'>
						<FaPlus className='me-1' /> {t('EXAMINATIONS.ADD_BUTTON', { letter: upperFirst(type.slice(0, 1)) })}
					</Button>
				</div>
			</Card.Header>
			<Card.Body>
				<Nav variant="tabs" activeKey={type} onSelect={(k) => changeType(k)} className="mb-3">
					{examinationTypes.map((item) => (
						<Nav.Item key={item.value}>
							<Nav.Link eventKey={item.value}>{item.label}</Nav.Link>
						</Nav.Item>
					))}
				</Nav>
				<Table
					columns={columns}
					data={examinations}
					loading={loading}
					handlePageChange={handlePageChange}
					perPage={perPage}
					handlePerRowsChange={handlePerRowsChange}
					total={total}
					handleSort={handleSort}
				/>
			</Card.Body>
			<ExaminationModel
				show={model}
				close={() => setModel(false)}
				type={type}
				fetch={fetchExamination}
			/>
		</Card>
	);
};

export default SysExaminations;